import React from 'react';
import styled from 'styled-components';
import {withRouter} from 'react-router-dom';

import {useApi} from '../../lib/api';
import {withUser} from '../../lib/user';
import Card from '../../components/Card';
import ListItem from '../../components/ListItem';
import TimeAgo from '../../components/TimeAgo';
import Icon from '../../components/Icon';
import {T} from '../../components/I18n';

import NoNotificationsIcon from './no-notifications.svg';
import { UserLink } from '../../components/UserLink';

const COLLAPSED_GROUPS_KEY = 'notificationList.collapsedGroups';

function loadCollapsedGroups() {
  try {
    return new Set(JSON.parse(window.localStorage.getItem(COLLAPSED_GROUPS_KEY)) || []);
  }
  catch(e) {
    // localStorage can be unavailable (e.g. private mode); every group then starts open.
    return new Set();
  }
}

function saveCollapsedGroups(collapsed) {
  try {
    window.localStorage.setItem(COLLAPSED_GROUPS_KEY, JSON.stringify([...collapsed]));
  }
  catch(e) {
    // Remembering which groups are collapsed is best-effort.
  }
}

function NotificationList({notifications, history}) {
  const api = useApi();
  // Notifications are owned by the screen, so keep track of the ones read from here to update them in place.
  const [readIds, setReadIds] = React.useState(() => new Set());
  // Groups the user has collapsed, remembered in this browser so a busy group stays out of the way between visits.
  const [collapsedGroups, setCollapsedGroups] = React.useState(loadCollapsedGroups);

  function toggleGroup(title) {
    setCollapsedGroups(prevCollapsed => {
      const nextCollapsed = new Set(prevCollapsed);

      if (nextCollapsed.has(title)) {
        nextCollapsed.delete(title);
      }
      else {
        nextCollapsed.add(title);
      }

      saveCollapsedGroups(nextCollapsed);
      return nextCollapsed;
    });
  }

  function isRead(notification) {
    return notification.read || readIds.has(notification.id);
  }

  async function markAsRead(notification) {
    try {
      await api.markNotificationAsRead(notification.id);
      setReadIds(prevReadIds => new Set(prevReadIds).add(notification.id));
    }
    catch(e) {
      // Nothing to do. The notification stays unread and can be marked again.
    }
  }

  function SheetRowReviewNotification(notification) {
    return {
      actions: [
        {
          title: 'Review', 
          onClick: () => {
            markAsRead(notification);
            history.push(`/sheet/${notification.metadata.templateId}/${notification.metadata.sheetId}/data/${notification.metadata.sheetRowId}`);
          }
        }
      ],
      render: (
        <T isolate><UserLink user={notification.from} /> submitted data to <strong><T variable="formname">{notification.metadata.templateName}</T></strong> for review.</T>
      )
    }
  }

  function SheetRowSecondLevelReviewNotification(notification) {
    return {
      actions: [
        {
          title: 'Review',
          onClick: () => {
            markAsRead(notification);
            history.push(`/sheet/${notification.metadata.templateId}/${notification.metadata.sheetId}/data/${notification.metadata.sheetRowId}`);
          }
        }
      ],
      render: (
        <T isolate><UserLink user={notification.from} /> approved submitted data to <strong><T variable="formname">{notification.metadata.templateName}</T></strong>. Awaiting second-level review.</T>
      )
    }
  }

  function ScaleErrorNotification(notification) {
    const {scaleId, errorMessage, templateId, scaleSerial} = notification.metadata || {};

    const goTo = to => () => {
      markAsRead(notification);
      history.push(to);
    };

    const actions = [
      {
        title: 'View Scale',
        onClick: goTo(scaleId ? `/devices/scale-configurations/${scaleId}` : '/devices/scale-configurations')
      }
    ];

    if (templateId) {
      actions.push({title: 'View Form', onClick: goTo(`/forms/${templateId}`)});
    }

    // Scale errors are informational, so they can be dismissed without navigating anywhere.
    actions.push({title: 'Mark as Read', onClick: () => markAsRead(notification)});

    return {
      actions,
      render: (
        <T isolate>A scale ({scaleSerial}) reported an error{errorMessage ? <>: <T noTranslate>{errorMessage}</T></> : '.'}</T>
      )
    }
  }

  const notificationRegistry = {
    'sheet_row_review_request': SheetRowReviewNotification,
    'sheet_row_second_level_review_request': SheetRowSecondLevelReviewNotification,
    'scale_error': ScaleErrorNotification
  };

  // Device notifications and review requests are shown in their own groups so one kind does not get lost among the
  // other. Any type that is not listed in a group still shows up, under "Other".
  const notificationGroups = [
    {title: 'Devices', types: ['scale_error']},
    {title: 'Reviews', types: ['sheet_row_review_request', 'sheet_row_second_level_review_request']}
  ];
  const groupedTypes = notificationGroups.flatMap(group => group.types);

  // The card is a queue of what still needs dealing with rather than a history, so anything already read drops out of
  // it. Types this version of the dashboard cannot render are ignored the same way.
  const supported = notifications.filter(notification =>
      notificationRegistry[notification.type] && !isRead(notification));

  // Each group keeps the order the notifications came in, and groups with nothing in them are left out entirely.
  const groups = [
    ...notificationGroups.map(group => ({
      title: group.title,
      notifications: supported.filter(notification => group.types.includes(notification.type))
    })),
    {
      title: 'Other',
      notifications: supported.filter(notification => !groupedTypes.includes(notification.type))
    }
  ].filter(group => group.notifications.length > 0);

  return (
    <CardContainer>
      <Card.Title>Notifications</Card.Title>
      {supported.length < 1 ? (
        <Card.Content>
          <NoNotifications>
            <img src={NoNotificationsIcon} alt="" />
            <h3>All caught up!</h3>
            <p>New notifications will appear here.</p>
          </NoNotifications>
        </Card.Content>
      ) : groups.map(group => (
        <React.Fragment key={group.title}>
          <GroupHeading type="button" aria-expanded={!collapsedGroups.has(group.title)} onClick={() => toggleGroup(group.title)}>
            <Icon name={collapsedGroups.has(group.title) ? 'chevron-right' : 'chevron-down'} />
            {group.title}
            {/* The count keeps a collapsed group from hiding how much is waiting in it. */}
            <GroupCount>{group.notifications.length}</GroupCount>
          </GroupHeading>
          {!collapsedGroups.has(group.title) && group.notifications.map(notification => {
            const handler = notificationRegistry[notification.type](notification);

            return (
              <Notification
                key={notification.id}
                unread
                subtitle={<TimeAgo date={notification.timestamp} />}
                options={handler.actions}>
                {handler.render}
              </Notification>
            );
          })}
        </React.Fragment>
      ))}
    </CardContainer>
  );
}

const CardContainer = styled(Card)`
  margin-bottom: 2rem;
`;

const NoNotifications = styled.div`
  text-align: center;

  h3 {
    margin: 1rem 0 0.5rem 0;
    font-size: 1.4rem;
    font-weight: 500;
  }

  p {
    padding: 0.5rem 0;
    margin: 0;
  }
`;

// Small label above each group, lined up with the notifications below it. Clicking it collapses or expands the group.
const GroupHeading = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  width: 100%;
  padding: 1rem 1.875rem 0.5rem 1.875rem;
  border: none;
  border-top: 0.0625rem solid #e4e4e4;
  background: none;
  color: #747474;
  font: inherit;
  font-size: 1.0rem;
  font-weight: 500;
  text-align: left;
  cursor: pointer;

  &:hover {
    color: #333333;
  }
`;

const GroupCount = styled.span`
  margin-left: auto;
  font-weight: 400;
  color: #747474;
`;

const Notification = styled(ListItem)`
  background: ${props => props.unread ? '#edf2fa' : 'white'};
`;

export default withRouter(withUser(NotificationList));